import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, XCircle, GraduationCap, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { attachWarningText } from '@/components/school-networks/AttachSchoolDialog';
import { checkSchoolCodeExists, type EtablissementSearchResult } from '@/api/schools';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import type { AddressSuggestion } from '@/api/schools';
import { EtablissementSearchInput } from '@/components/schools/EtablissementSearchInput';
import { AcademieSelect } from '@/components/schools/AcademieSelect';
import { getCountryOptions, countryCodeToFlag } from '@/constants/countries';
import type { WizardFormData } from '../types';
import type { SchoolOptions } from '@/types/school';

const { priority: PRIORITY_COUNTRIES, others: OTHER_COUNTRIES } = getCountryOptions();

interface InfoStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
  errors?: Record<string, string>;
  onCodeValidation?: (isValid: boolean, error?: string) => void;
  /** Mode passage V2 : le code identifie l'école existante, il n'est pas modifiable */
  codeReadOnly?: boolean;
  /**
   * Le réseau physique ne se choisit qu'à la création. Sur une école existante
   * il est soit déjà acquis (définitif), soit à traiter au déploiement V2 —
   * proposer un sélecteur sans effet ici tromperait.
   */
  physicalNetworkEditable?: boolean;
}

export function InfoStep({ formData, onChange, options, errors, onCodeValidation, codeReadOnly = false, physicalNetworkEditable = true }: InfoStepProps) {
  const physicalNetworks = (options?.networks || []).filter((n) => n.type === 'physical');
  const selectedPhysicalNetwork =
    physicalNetworks.find((n) => n.id === formData.physicalNetworkId) || null;
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeExists, setCodeExists] = useState<boolean | null>(null);

  const handleSelectResult = (result: EtablissementSearchResult) => {
    const updates: Partial<WizardFormData> = {};

    // Un champ est considéré vide s'il n'a pas de valeur exploitable
    const isEmpty = (value?: string) => !value || value.trim() === '';

    // Toujours mettre à jour RNE et académie (c'est l'objet de la sélection)
    updates.rne = result.rne || '';
    updates.codeAcademie = result.codeAcademie || '';

    // Nom : on complète toujours le libellé depuis l'annuaire EN,
    // mais on ne régénère le code école que s'il est encore vide.
    if (result.name) {
      updates.name = result.name;
      if (isEmpty(formData.code)) {
        updates.code = generateCode(result.name);
      }
    }

    // Adresse du siège social : on ne l'écrase pas si elle est déjà renseignée,
    // seulement si elle est vide (l'adresse de l'établissement, elle, est écrasable).
    if ((result.address || result.postalCode || result.city) && isEmpty(formData.address)) {
      const fullAddress = [result.address, result.postalCode, result.city].filter(Boolean).join(' ');
      updates.address = fullAddress;
    }

    // Contact : ne pas écraser une valeur déjà saisie, seulement compléter si vide
    if (result.email && isEmpty(formData.email)) {
      updates.email = result.email;
    }
    if (result.phone && isEmpty(formData.phone)) {
      updates.phone = result.phone;
    }

    // Store coordinates from EN API
    if (result.latitude && result.longitude) {
      updates.latitude = result.latitude;
      updates.longitude = result.longitude;
    }

    onChange(updates);
  };

  const generateCode = (name: string): string => {
    // Generate a trigram from the name (first letter of each significant word)
    const normalized = name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .toUpperCase();

    // Filter out common words and get first letters
    const excludeWords = ['DE', 'DU', 'DES', 'LA', 'LE', 'LES', 'ET', 'L', 'D', 'A', 'AU', 'AUX'];
    const words = normalized.split(/[^A-Z0-9]+/).filter(w => w && !excludeWords.includes(w));

    if (words.length >= 3) {
      // Take first letter of first 3 significant words
      return words.slice(0, 3).map(w => w[0]).join('');
    } else if (words.length === 2) {
      // Take first letter of each word + second letter of longest word
      const sorted = [...words].sort((a, b) => b.length - a.length);
      return words[0][0] + words[1][0] + (sorted[0].length > 1 ? sorted[0][1] : '');
    } else if (words.length === 1 && words[0].length >= 3) {
      // Take first 3 letters
      return words[0].substring(0, 3);
    }

    return normalized.replace(/[^A-Z0-9]/g, '').substring(0, 3);
  };

  // Debounced code check
  useEffect(() => {
    if (codeReadOnly) {
      setCodeExists(null);
      return;
    }
    if (!formData.code || formData.code.length < 2) {
      setCodeExists(null);
      return;
    }

    // Validate format first
    if (!/^[A-Z0-9]+$/.test(formData.code)) {
      setCodeExists(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingCode(true);
      try {
        const exists = await checkSchoolCodeExists(formData.code);
        setCodeExists(exists);
        if (onCodeValidation) {
          onCodeValidation(!exists, exists ? `Le code "${formData.code}" est déjà utilisé par une autre école` : undefined);
        }
      } catch (err) {
        console.error('Error checking code:', err);
        setCodeExists(null);
      } finally {
        setCheckingCode(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.code, onCodeValidation, codeReadOnly]);

  const handleNameChange = (name: string) => {
    const previousCode = generateCode(formData.name);
    onChange({ name });
    // Auto-generate code if not manually edited
    if (!codeReadOnly && (!formData.code || formData.code === previousCode)) {
      onChange({ code: generateCode(name) });
    }
  };

  const handleCodeChange = (code: string) => {
    setCodeExists(null); // Reset while typing
    onChange({ code: code.toUpperCase().replace(/[^A-Z0-9]/g, '') });
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">
          Informations de base
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Renseignez les informations principales de l'école
        </p>
      </div>

      <div className="grid gap-6">
        {/* Education Nationale Toggle */}
        <div className="p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <GraduationCap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <Label htmlFor="isEducationNationale" className="text-base font-medium cursor-pointer">
                  Etablissement Education Nationale
                </Label>
                <p className="text-sm text-muted-foreground">
                  École reconnue par l'Éducation Nationale française
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                type="button"
                className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                  formData.isEducationNationale
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => onChange({ isEducationNationale: true })}
              >
                Oui
              </button>
              <button
                type="button"
                className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                  !formData.isEducationNationale
                    ? 'bg-white shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => {
                  onChange({ isEducationNationale: false, rne: '', codeAcademie: '' });
                }}
              >
                Non
              </button>
            </div>
          </div>
        </div>

        {/* RNE and Academie Section - Only shown if Education Nationale */}
        {formData.isEducationNationale && (
          <div className="pb-4 border-b">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">
              Recherche dans l'annuaire Education Nationale
            </h3>

            {/* Search by RNE or name */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="etablissement-search">Rechercher un établissement</Label>
                <EtablissementSearchInput onSelect={handleSelectResult} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rne">Code RNE (UAI)</Label>
                  <Input
                    id="rne"
                    value={formData.rne}
                    onChange={(e) => onChange({ rne: e.target.value.toUpperCase() })}
                    placeholder="Ex: 0750001A"
                    className={errors?.rne ? 'border-red-500' : ''}
                  />
                  {errors?.rne ? (
                    <p className="text-xs text-red-500">{errors.rne}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Rempli automatiquement lors de la selection
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="codeAcademie">Académie</Label>
                  <AcademieSelect
                    value={formData.codeAcademie}
                    onChange={(codeAcademie) => onChange({ codeAcademie })}
                  />
                  {errors?.codeAcademie ? (
                    <p className="text-xs text-red-500">{errors.codeAcademie}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Rempli automatiquement lors de la sélection
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Name and Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nom de l'école <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex: École Saint-Joseph"
              className={errors?.name ? 'border-red-500' : ''}
            />
            {errors?.name && (
              <p className="text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">
              Code <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="Ex: ESJ"
                disabled={codeReadOnly}
                className={errors?.code || codeExists ? 'border-red-500 pr-10' : codeExists === false ? 'border-green-500 pr-10' : ''}
              />
              {checkingCode && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!checkingCode && codeExists === false && formData.code && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
              )}
              {!checkingCode && codeExists === true && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <XCircle className="h-4 w-4 text-red-500" />
                </div>
              )}
            </div>
            {errors?.code && (
              <p className="text-xs text-red-500">{errors.code}</p>
            )}
            {!errors?.code && codeExists === true && (
              <p className="text-xs text-red-500">
                Ce code est déjà utilisé par une autre école. Veuillez en choisir un autre.
              </p>
            )}
            {!errors?.code && codeExists === false && (
              <p className="text-xs text-green-600">
                Ce code est disponible
              </p>
            )}
            {codeExists === null && !errors?.code && (
              <p className="text-xs text-muted-foreground">
                {codeReadOnly
                  ? "Le code identifie l'école existante, il n'est pas modifiable"
                  : "Trigramme unique de l'école (généré automatiquement)"}
              </p>
            )}
          </div>
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email de contact</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => onChange({ email: e.target.value })}
              placeholder="contact@ecole.fr"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telephone</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => onChange({ phone: e.target.value })}
              placeholder="01 23 45 67 89"
            />
          </div>
        </div>

        {/* Email Sender */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emailSender">
              Email expediteur <span className="text-red-500">*</span>
            </Label>
            <Input
              id="emailSender"
              type="email"
              value={formData.emailSender}
              onChange={(e) => onChange({ emailSender: e.target.value })}
              placeholder="noreply@ecole.fr"
              className={errors?.emailSender ? 'border-red-500' : ''}
            />
            {errors?.emailSender && (
              <p className="text-xs text-red-500">{errors.emailSender}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Adresse utilisee pour l'envoi des emails
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="domainSender">Domaine expediteur</Label>
            <Input
              id="domainSender"
              value={formData.domainSender}
              onChange={(e) => onChange({ domainSender: e.target.value })}
              placeholder="ecole.fr"
            />
          </div>
        </div>

        {/* Country */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="country">Pays</Label>
            <Select
              value={formData.country || 'FR'}
              onValueChange={(value) => onChange({ country: value, latitude: null, longitude: null })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un pays" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
                <div className="border-t my-1" />
                {OTHER_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <AddressAutocomplete
              id="address"
              value={formData.address}
              country={formData.country || 'FR'}
              onChange={(address) => onChange({ address })}
              onSelect={(suggestion: AddressSuggestion) => {
                onChange({
                  address: suggestion.label,
                  latitude: suggestion.latitude,
                  longitude: suggestion.longitude,
                });
              }}
              placeholder="12 rue de l'École, 75001 Paris"
            />
            <Input
              id="addressComplement"
              value={formData.addressComplement}
              onChange={(e) => onChange({ addressComplement: e.target.value })}
              placeholder="Complément d'adresse"
            />
            {errors?.address && (
              <p className="text-xs text-red-500">{errors.address}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressHeadoffice">Siège social</Label>
            <AddressAutocomplete
              id="addressHeadoffice"
              value={formData.addressHeadoffice}
              country={formData.country || 'FR'}
              onChange={(addressHeadoffice) => onChange({ addressHeadoffice })}
              onSelect={(suggestion: AddressSuggestion) => {
                onChange({ addressHeadoffice: suggestion.label });
              }}
              placeholder="Adresse du siège social"
            />
            <Input
              id="addressHeadofficeComplement"
              value={formData.addressHeadofficeComplement}
              onChange={(e) => onChange({ addressHeadofficeComplement: e.target.value })}
              placeholder="Complément d'adresse"
            />
          </div>
        </div>

        {/* Réseaux : virtuel (simple agrégation, modifiable) et physique
            (marque blanche + tenant group V2, rattachement définitif). Choisir
            le réseau physique ici fait naître l'école dedans : son tenant V2
            est créé directement dans le bon groupe, sans déplacement ensuite. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="network">Réseau virtuel</Label>
            <Select
              value={formData.networkId?.toString() || 'none'}
              onValueChange={(value) =>
                onChange({ networkId: value === 'none' ? null : parseInt(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner un reseau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {options?.networks
                  .filter((network) => network.type !== 'physical')
                  .map((network) => (
                    <SelectItem key={network.id} value={network.id.toString()}>
                      {network.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="physicalNetwork">Réseau physique (marque blanche)</Label>
            {!physicalNetworkEditable ? (
              <p className="text-sm text-muted-foreground pt-2">
                {selectedPhysicalNetwork
                  ? `${selectedPhysicalNetwork.name} — rattachement définitif`
                  : 'Aucun — se choisit au déploiement V2'}
              </p>
            ) : (
            <Select
              value={formData.physicalNetworkId?.toString() || 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  onChange({ physicalNetworkId: null, v2Host: '' });
                  return;
                }
                const id = parseInt(value);
                const network = options?.networks.find((n) => n.id === id);
                // Host prérempli avec celui du réseau : une école qui n'a pas
                // son propre sous-domaine partage simplement celui du réseau.
                onChange({ physicalNetworkId: id, v2Host: network?.v2Host || '' });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Aucun (groupe École Futée)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun (groupe École Futée)</SelectItem>
                {physicalNetworks.map((network) => (
                  <SelectItem key={network.id} value={network.id.toString()}>
                    {network.name}
                    {network.v2Host ? ` (${network.v2Host})` : ' — sans host V2'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
          </div>
        </div>

        {physicalNetworkEditable && selectedPhysicalNetwork && (
          <div className="space-y-2">
            <Label htmlFor="v2Host">Domaine de connexion de l'école</Label>
            <Input
              id="v2Host"
              value={formData.v2Host}
              onChange={(e) => onChange({ v2Host: e.target.value })}
              placeholder={selectedPhysicalNetwork.v2Host || 'app.mon-reseau.fr'}
            />
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {attachWarningText(
                  formData.name || 'cette école',
                  selectedPhysicalNetwork.name,
                  formData.v2Host || selectedPhysicalNetwork.v2Host || '—'
                )}
              </AlertDescription>
            </Alert>
            {!selectedPhysicalNetwork.v2TenantGroupId && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ce réseau n'a pas encore de tenant group V2 : le rattachement fonctionnera,
                  mais le déploiement V2 restera bloqué tant que le réseau n'est pas initialisé.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
